/**
 * Data layer for the `/notifications` route (web/docs/data-layer.md). Mirrors
 * the mobile real client (`getNotifications` / `getUnreadCount` /
 * `markNotificationsRead` / `respondToRequest` in `app/src/data/api-real.ts`)
 * over `src/api/http.ts` + `@shared/mappers`:
 *
 * - `['notifications']` → `GET /v1/notifications?per_page=&cursor=` — one
 *   newest-first feed (docs/api-schema.md §Social) shared by two surfaces:
 *   `/notifications` shows the people slice, the Discussions inbox
 *   (`features/inbox`) the `comment_reply` / `comment_mention` slices, and
 *   the Discussions nav badge counts unread comment rows on its first page.
 *   Rows are `NotifEntry` (the shared mapper + the optional
 *   `comment_preview` / `title` hydration).
 * - `['notifications','unread']` → `GET /v1/notifications/unread-count`.
 * - `PUT /v1/notifications/mark-read` marks everything read — there is no
 *   per-row or per-type variant (the backend route is a PUT —
 *   `backend/src/routes/social.py` — matching the mobile client), so both
 *   surfaces clear the whole feed; optimistically: patch in `onMutate`, roll back in `onError`,
 *   invalidate in `onSettled`, toast + Retry on failure.
 * - `POST /v1/connections/requests/{username}/approve|decline` resolves a
 *   pending follow request; the row keeps its own acted-on state (like the
 *   app) and the list refetches on settle.
 *
 * Reads degrade on `404` the same way the app does.
 */

import { useEffect, useMemo, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiGet, apiPost, apiPut, ApiError } from '@/api/http';
import { translate } from '@/i18n/locale';

import type {
  ApiApproveResponse,
  ApiDeclineResponse,
  ApiMarkedRead,
  ApiNotification,
  ApiNotificationsPage,
  ApiUnreadCount,
} from '@shared/api-types';
import { imageUrls, mapNotifItem, paletteFromTitle } from '@shared/mappers';
import type { NotifItem } from '@shared/types';

export const NOTIFICATIONS_KEY = ['notifications'] as const;
export const NOTIFICATIONS_UNREAD_KEY = ['notifications', 'unread'] as const;

const PER_PAGE = 30;

/**
 * Both notification surfaces mark the feed read shortly after unread rows
 * first render (the app marks on open); this is that delay.
 */
export const AUTO_MARK_READ_DELAY_MS = 800;

/* ------------------------------------------------------------------ */
/* Row model — `NotifItem` + the optional comment hydration            */
/* ------------------------------------------------------------------ */

/**
 * `comment_preview` on a `comment_reply` / `comment_mention` row
 * (docs/api-schema.md §GET /v1/notifications). `body` is only ever non-empty
 * for `visible`; any state the client does not know is treated as `hidden`
 * so moderated text is never rendered.
 */
export interface NotifCommentPreview {
  body: string;
  hasImage: boolean;
  state: 'visible' | 'deleted' | 'hidden';
}

/** The lean catalog card (`title`) on a comment notification. */
export interface NotifTitleCard {
  tmdbId: string;
  titleType: 'show' | 'movie';
  title: string;
  titleBr?: string;
  posterUrl?: string;
  posterFallbackUrl?: string;
  palette: [string, string];
  seasonNumber?: number;
  episodeNumber?: number;
}

/**
 * One notification row as the web renders it. The shared `mapNotifItem`
 * (app/src/data/mappers.ts) does not map the additive `comment_preview` /
 * `title` fields yet, so they are mapped here on top of it — both stay
 * optional: an older backend (or an unresolvable comment/catalog row) omits
 * them and every surface degrades without fetching anything per row.
 */
export interface NotifEntry extends NotifItem {
  preview?: NotifCommentPreview;
  titleCard?: NotifTitleCard;
}

export interface NotifEntriesPage {
  items: NotifEntry[];
  cursor?: string;
}

/** Wire → row. Never throws on a malformed optional field — it is dropped. */
export function mapNotifEntry(n: ApiNotification): NotifEntry {
  const entry: NotifEntry = mapNotifItem(n);
  const preview = n.comment_preview;
  if (preview && typeof preview === 'object') {
    const state: NotifCommentPreview['state'] =
      preview.state === 'visible' || preview.state === 'deleted' ? preview.state : 'hidden';
    entry.preview = {
      body: state === 'visible' && typeof preview.body === 'string' ? preview.body.trim() : '',
      hasImage: preview.has_image === true,
      state,
    };
  }
  const card = n.title;
  if (card && typeof card === 'object' && card.tmdb_id) {
    const poster = imageUrls(card.poster_key, card.poster_path, 'w185');
    const name = (card.title ?? '').trim();
    entry.titleCard = {
      tmdbId: String(card.tmdb_id),
      titleType: card.title_type === 'movie' ? 'movie' : 'show',
      title: name,
      titleBr: card.title_br?.trim() || undefined,
      posterUrl: poster.primary ?? undefined,
      posterFallbackUrl: poster.fallback ?? undefined,
      palette: paletteFromTitle(name || String(card.tmdb_id)),
      seasonNumber: card.season_number,
      episodeNumber: card.episode_number,
    };
  }
  return entry;
}

const COMMENT_NOTIF_TYPES: ReadonlySet<NotifItem['type']> = new Set([
  'comment_reply',
  'comment_mention',
]);

/** Comment events live in the Discussions inbox (`/discussions`). */
export function isCommentNotif(item: Pick<NotifItem, 'type'>): boolean {
  return COMMENT_NOTIF_TYPES.has(item.type);
}

/** People events (follow / request / approve / decline / resolved) live on `/notifications`. */
export function isPeopleNotif(item: Pick<NotifItem, 'type'>): boolean {
  return !isCommentNotif(item);
}

/** `GET /v1/notifications` — one cursor page, newest first. */
export async function fetchNotificationsPage(cursor?: string): Promise<NotifEntriesPage> {
  const query: Record<string, string | number | undefined> = { per_page: PER_PAGE };
  if (cursor) query.cursor = cursor;
  try {
    const r = await apiGet<ApiNotificationsPage>('/v1/notifications', { query });
    return {
      items: (r.notifications ?? []).map(mapNotifEntry),
      cursor: r.cursor ?? undefined,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { items: [] };
    throw err;
  }
}

export function useNotifications() {
  return useInfiniteQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: ({ pageParam }) => fetchNotificationsPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
  });
}

/** Flatten an infinite query's pages into one newest-first item list. */
export function flattenNotificationPages(
  data: ReturnType<typeof useNotifications>['data'],
): NotifEntry[] {
  return data?.pages.flatMap((p) => p.items) ?? [];
}

/** The slices of the one notification feed each screen shows. */
export type NotifSlice = 'people' | 'comment_reply' | 'comment_mention';

function inSlice(item: NotifEntry, slice: NotifSlice): boolean {
  return slice === 'people' ? isPeopleNotif(item) : item.type === slice;
}

/** Rows a filtered surface wants before it stops auto-loading pages. */
export const MIN_FILTERED_ROWS = 10;

/** Upper bound on pages auto-loaded to fill a sparse filter (then the sentinel takes over). */
const MAX_FILL_PAGES = 8;

/**
 * One slice of the shared `['notifications']` feed. The server cannot filter
 * by type, so a slice can come back sparse (a page of 30 follows has no
 * replies); while `active`, keep loading pages until the slice has at least
 * `MIN_FILTERED_ROWS` rows or the cursor ends (bounded by `MAX_FILL_PAGES`).
 * `filling` is true while that is still happening, so callers can keep a
 * skeleton up instead of flashing an empty state.
 */
export function useNotificationSlice(slice: NotifSlice, active = true) {
  const query = useNotifications();
  const all = useMemo(() => flattenNotificationPages(query.data), [query.data]);
  const items = useMemo(() => all.filter((n) => inSlice(n, slice)), [all, slice]);
  const pageCount = query.data?.pages.length ?? 0;
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;
  const wantsMore =
    active &&
    items.length < MIN_FILTERED_ROWS &&
    hasNextPage &&
    !isError &&
    pageCount > 0 &&
    pageCount < MAX_FILL_PAGES;
  useEffect(() => {
    if (wantsMore && !isFetchingNextPage) void fetchNextPage();
  }, [wantsMore, isFetchingNextPage, fetchNextPage, pageCount]);
  return { query, items, filling: wantsMore };
}

/** `GET /v1/notifications/unread-count`. */
export async function fetchUnreadCount(): Promise<number> {
  try {
    const r = await apiGet<ApiUnreadCount>('/v1/notifications/unread-count');
    return r.unread ?? 0;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return 0;
    throw err;
  }
}

export function useUnreadCount() {
  return useQuery({ queryKey: NOTIFICATIONS_UNREAD_KEY, queryFn: fetchUnreadCount });
}

/** Unread `comment_reply` + `comment_mention` rows in one page of the feed. */
export function countUnreadComments(items: readonly NotifEntry[]): number {
  return items.reduce((n, item) => (isCommentNotif(item) && !item.read ? n + 1 : n), 0);
}

/**
 * The Discussions badge: unread reply + mention notifications, counted from
 * the cached FIRST page of `['notifications']` (cheap — no per-row fetch, no
 * extra poller). The list rides the same cadence as the unread-count query
 * the nav already runs: when the server's unread total goes up, the list is
 * invalidated once so the badge picks up the new rows.
 */
export function useUnreadCommentCount(): number {
  const qc = useQueryClient();
  const list = useNotifications();
  const total = useUnreadCount().data;
  const prevTotal = useRef(total);
  useEffect(() => {
    const prev = prevTotal.current;
    prevTotal.current = total;
    if (prev !== undefined && total !== undefined && total > prev) {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY, exact: true });
    }
  }, [qc, total]);
  const firstPage = list.data?.pages[0]?.items;
  return useMemo(() => countUnreadComments(firstPage ?? []), [firstPage]);
}

function withAllRead(data: InfiniteData<NotifEntriesPage> | undefined) {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => (item.read ? item : { ...item, read: true })),
    })),
  };
}

/** `PUT /v1/notifications/mark-read` — marks everything read, optimistically. */
export function useMarkAllRead() {
  const qc = useQueryClient();
  // The ref lets the toast's Retry action re-enter the same mutation without
  // closing over a stale `mutate` (same pattern as show/api.ts).
  const mutateRef = useRef<() => void>(() => {});
  const mutation = useMutation({
    mutationFn: () => apiPut<ApiMarkedRead>('/v1/notifications/mark-read'),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_KEY });
      await qc.cancelQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY });
      const prevList = qc.getQueryData<InfiniteData<NotifEntriesPage>>(NOTIFICATIONS_KEY);
      const prevUnread = qc.getQueryData<number>(NOTIFICATIONS_UNREAD_KEY);
      qc.setQueryData<InfiniteData<NotifEntriesPage>>(NOTIFICATIONS_KEY, withAllRead);
      qc.setQueryData(NOTIFICATIONS_UNREAD_KEY, 0);
      return { prevList, prevUnread };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prevList !== undefined) qc.setQueryData(NOTIFICATIONS_KEY, ctx.prevList);
      if (ctx?.prevUnread !== undefined) qc.setQueryData(NOTIFICATIONS_UNREAD_KEY, ctx.prevUnread);
      toast.error(translate('notif.markReadError'), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current() },
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY });
    },
  });
  mutateRef.current = mutation.mutate;
  return mutation;
}

export interface RespondVars {
  username: string;
  approve: boolean;
}

/** `POST /v1/connections/requests/{username}/approve | /decline`. */
export function useRespondToRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ username, approve }: RespondVars) => {
      const path = `/v1/connections/requests/${encodeURIComponent(username)}/${
        approve ? 'approve' : 'decline'
      }`;
      if (approve) return apiPost<ApiApproveResponse>(path);
      return apiPost<ApiDeclineResponse>(path);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}
